import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  changeTeacherPassword,
  confirmTeacherEmailChange,
  fetchTeacherProfile,
  requestTeacherEmailChange,
  updateTeacherProfile,
  uploadCurrentTeacherProfilePicture,
} from "../../../redux/Actions/teacherProfileActions";
import Meta from "../../Meta/Meta";
import TeacherNavbar from "./TeacherNavbar";
import "./DashboardLayout.css";

const emptyProfile = {
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  job: "",
  nationality: "",
  accommodation: "",
  phoneNumber: "",
  bio: "",
  study: "",
  experience: "",
  specialization: "",
  academicQualifications: "",
  courseDescription: "",
};

const getErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string") return data;
  return data?.message || fallback;
};

const getImageSrc = (value) => {
  if (!value) return "";
  const src = String(value).trim();
  if (src.startsWith("/api/")) return `${process.env.REACT_APP_API_URL}${src}`;
  if (/^(https?:)?\/\//i.test(src) || src.startsWith("/")) return src;
  return `/${src}`;
};

const TeacherProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const profile = useSelector((state) => state.teacherProfile.profile);
  const [form, setForm] = useState(emptyProfile);
  const [loading, setLoading] = useState(!profile);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [password, setPassword] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [emailChange, setEmailChange] = useState({
    currentPassword: "",
    newEmail: "",
    code: "",
  });
  const [emailCodeSent, setEmailCodeSent] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    dispatch(fetchTeacherProfile()).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [dispatch]);

  useEffect(() => {
    if (!profile) return;
    setForm({
      ...emptyProfile,
      ...Object.fromEntries(
        Object.keys(emptyProfile).map((key) => [key, profile[key] || ""])
      ),
      dateOfBirth: profile.dateOfBirth
        ? new Date(profile.dateOfBirth).toISOString().slice(0, 10)
        : "",
    });
  }, [profile]);

  const updateField = (setter) => (event) => {
    const { name, value } = event.target;
    setter((current) => ({ ...current, [name]: value }));
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      await dispatch(updateTeacherProfile(form));
      setMessage({ type: "success", text: "Your profile was updated successfully." });
    } catch (error) {
      setMessage({
        type: "error",
        text: getErrorMessage(error, "The profile could not be updated."),
      });
    } finally {
      setSaving(false);
    }
  };

  const uploadPicture = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setSaving(true);
    setMessage(null);
    try {
      await dispatch(uploadCurrentTeacherProfilePicture(file));
      setMessage({ type: "success", text: "Profile picture updated." });
    } catch (error) {
      setMessage({ type: "error", text: getErrorMessage(error, "Picture upload failed.") });
    } finally {
      event.target.value = "";
      setSaving(false);
    }
  };

  const reauthenticate = () => {
    dispatch({ type: "LOGOUT" });
    navigate("/login", { replace: true });
  };

  const submitPassword = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      await changeTeacherPassword(password);
      setMessage({ type: "success", text: "Password updated. Redirecting to sign in…" });
      window.setTimeout(reauthenticate, 1200);
    } catch (error) {
      setMessage({ type: "error", text: getErrorMessage(error, "Password update failed.") });
    } finally {
      setSaving(false);
    }
  };

  const requestEmail = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      await requestTeacherEmailChange({
        currentPassword: emailChange.currentPassword,
        newEmail: emailChange.newEmail,
      });
      setEmailCodeSent(true);
      setMessage({ type: "success", text: "A confirmation code was sent to the new email." });
    } catch (error) {
      setMessage({ type: "error", text: getErrorMessage(error, "Could not send the code.") });
    } finally {
      setSaving(false);
    }
  };

  const confirmEmail = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await confirmTeacherEmailChange(emailChange.code);
      setMessage({ type: "success", text: "Email updated. Redirecting to sign in…" });
      window.setTimeout(reauthenticate, 1200);
    } catch (error) {
      setMessage({ type: "error", text: getErrorMessage(error, "Email confirmation failed.") });
    } finally {
      setSaving(false);
    }
  };

  const personalFields = [
    ["firstName", "First name"],
    ["lastName", "Last name"],
    ["dateOfBirth", "Date of birth", "date"],
    ["phoneNumber", "Phone number", "tel"],
    ["nationality", "Nationality"],
    ["accommodation", "Current residence"],
    ["job", "Education / profession"],
  ];
  const publicFields = [
    ["bio", "Biography"],
    ["study", "Studies"],
    ["experience", "Experience"],
    ["specialization", "Specialization"],
    ["academicQualifications", "Academic qualifications"],
    ["courseDescription", "Course description"],
  ];

  return (
    <>
      <Meta title="My Teacher Profile | ALGermanist" description="Manage your teacher profile and account security." />
      <div className="dashboard-layout" dir="ltr">
        <TeacherNavbar />
        <Box className="dashboard-teacher-content" sx={{ py: 4 }}>
          <Box sx={{ maxWidth: 1050, mx: "auto" }}>
            <Typography variant="h4" fontWeight={800} mb={3}>My Profile</Typography>
            {message && <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
            {loading ? (
              <Box textAlign="center" py={8}><CircularProgress /></Box>
            ) : (
              <>
                <Paper sx={{ p: { xs: 2, md: 4 }, mb: 3 }}>
                  <Box display="flex" alignItems="center" gap={3} mb={3} flexWrap="wrap">
                    <Avatar src={getImageSrc(profile?.profilePicture)} sx={{ width: 96, height: 96 }}>
                      {profile?.firstName?.[0]}{profile?.lastName?.[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="h6">Profile picture</Typography>
                      <Button component="label" variant="outlined" sx={{ mt: 1 }} disabled={saving}>
                        Upload picture
                        <input hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadPicture} />
                      </Button>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  <Typography variant="h6" fontWeight={700} mb={2}>Personal information</Typography>
                  <Box component="form" onSubmit={saveProfile}>
                    <Grid container spacing={2}>
                      {personalFields.map(([name, label, type = "text"]) => (
                        <Grid item xs={12} sm={6} key={name}>
                          <TextField fullWidth required={name === "firstName" || name === "lastName"} name={name} label={label} type={type} value={form[name]} onChange={updateField(setForm)} InputLabelProps={type === "date" ? { shrink: true } : undefined} />
                        </Grid>
                      ))}
                    </Grid>
                    <Typography variant="h6" fontWeight={700} mt={4} mb={2}>Public teacher information</Typography>
                    <Grid container spacing={2}>
                      {publicFields.map(([name, label]) => (
                        <Grid item xs={12} sm={6} key={name}>
                          <TextField fullWidth multiline minRows={3} name={name} label={label} value={form[name]} onChange={updateField(setForm)} />
                        </Grid>
                      ))}
                    </Grid>
                    <Button type="submit" variant="contained" disabled={saving} sx={{ mt: 3, bgcolor: "#245D51" }}>Save profile</Button>
                  </Box>
                </Paper>

                <Paper component="form" onSubmit={submitPassword} sx={{ p: { xs: 2, md: 4 }, mb: 3 }}>
                  <Typography variant="h6" fontWeight={700} mb={1}>Change password</Typography>
                  <Typography color="text.secondary" mb={2}>Changing your password signs you out on all devices.</Typography>
                  <Grid container spacing={2}>
                    {[ ["currentPassword", "Current password"], ["newPassword", "New password"], ["confirmPassword", "Confirm new password"] ].map(([name, label]) => (
                      <Grid item xs={12} md={4} key={name}><TextField fullWidth required type="password" name={name} label={label} value={password[name]} onChange={updateField(setPassword)} /></Grid>
                    ))}
                  </Grid>
                  <Button type="submit" variant="contained" disabled={saving} sx={{ mt: 3, bgcolor: "#245D51" }}>Update password</Button>
                </Paper>

                <Paper component="form" onSubmit={requestEmail} sx={{ p: { xs: 2, md: 4 } }}>
                  <Typography variant="h6" fontWeight={700} mb={1}>Change email address</Typography>
                  <Typography color="text.secondary" mb={2}>Current email: {profile?.email}</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}><TextField fullWidth required type="email" name="newEmail" label="New email" value={emailChange.newEmail} onChange={updateField(setEmailChange)} /></Grid>
                    <Grid item xs={12} sm={6}><TextField fullWidth required type="password" name="currentPassword" label="Current password" value={emailChange.currentPassword} onChange={updateField(setEmailChange)} /></Grid>
                  </Grid>
                  <Button type="submit" variant="outlined" disabled={saving} sx={{ mt: 3 }}>Send confirmation code</Button>
                  {emailCodeSent && (
                    <Box display="flex" gap={2} alignItems="center" mt={3} flexWrap="wrap">
                      <TextField required name="code" label="Six-digit confirmation code" value={emailChange.code} onChange={updateField(setEmailChange)} inputProps={{ maxLength: 6, inputMode: "numeric" }} />
                      <Button type="button" variant="contained" disabled={saving || emailChange.code.length !== 6} onClick={confirmEmail} sx={{ bgcolor: "#245D51" }}>Confirm new email</Button>
                    </Box>
                  )}
                </Paper>
              </>
            )}
          </Box>
        </Box>
      </div>
    </>
  );
};

export default TeacherProfile;
